import React, {
  Suspense, lazy,
} from 'react';
import {
  Grid, Heading, Container, Section, Empty,
  RichText,
  Badge,
} from '@printdeal/ui-library';
import { useLocation } from '@gatsbyjs/reach-router';
import { SelectorContent } from '@printdeal/product-selector';
import { Experiment, StoreToggle } from '@printdeal/feature-management/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Markdown } from '@printdeal/markdown/react';
import clsx from 'clsx';
import type { IProductPage, IPortalConfiguration } from '../../../../types/product/productDetailPage';
import { getSizeTableTab } from '../../../../helpers/ProductHelper';
import Breadcrumb from '../../../Breadcrumb';
import { DefaultValuesSelectorSection } from '../../selector/components/DefaultValues/DefaultValuesSelectorSection';
import '../../../../css/easy-product-page.css';
import ResetSelectorButton from '../../selector/components/ResetSelectorButton';
import ReviewsIO from '../reviews/ReviewsIO';
import { REVIEWS_IO_THRESHOLD } from '../../../../helpers/reviewHelper';
import ClientOnly from '../../../ClientOnly';
import LinkedFaqs from '../LinkedFaqs';
import { OrderProgress } from '../OrderProgress';
import { ProductGalleryBuilder } from '../ProductGalleryBuilder';
import PersonalAssistance from '../PersonalAssistance';
import { ExploreDesignTemplateButton } from '../../../design-template/exploreDesignTemplateButton';
import { FallbackSpinnerComponent } from '../../../FallbackSpinnerComponent';
import { useSaveUserSelection } from '../useSelectionOnWindow';
import { ProductTabs } from '../advanced/ProductTabs';
import TechnicalDrawing from '../../selector/components/TechnicalDrawing';
import { getBadgeKind } from '../../selector/helpers/AttributeValueHelper';
import PPAGBanner from '../PPAGBanner';
import { Label } from '../../../product-category/filters/results';
import { useCTRecommendations } from '../../../recommendations/hooks/useCTRecommendations';
import Recommendations from '../../../recommendations/Recommendations';
import { handleRecommendationProductImpressions } from '../../../../helpers/dataLayerHelpers/productDataLayerHelper';

const SizeTable = lazy(() => import(/* webpackChunkName: "SizeTable" */ '../SizeTable'));
const TechnicalDrawingModal = lazy(() => import(/* webpackChunkName: "TechnicalDrawingModal" */ '../../selector/components/Modals/TechnicalDrawingModal'));

interface Props {
  selector?: SelectorContent;
  data: {
    pageContent: IProductPage;
    portalConfiguration: IPortalConfiguration,
  };
  isSelectorInitialized?: boolean;
}

const ProductDetailPPAG = ({ selector, data, isSelectorInitialized = true }: Props) => {
  const {
    pageContent,
    pageContent: {
      productName,
      title,
      subtitle,
      productCategory,
      gallery,
      scenes,
      reviews: staticReviews,
      productReviews: reviewsIO,
      contentful_id: contentfulId,
      productTabs,
      labels,
    },
  } = data;
  useSaveUserSelection();
  const location = useLocation() as Location;
  const pageTitle = title || productName || '';

  // Switch to Reviews.io when there are more than 3 reviews.
  const reviewSource = reviewsIO.results_count < REVIEWS_IO_THRESHOLD ? 'static' : 'reviews_io';
  const reviews = reviewSource === 'static' ? staticReviews : reviewsIO;
  const hasProductReviews = reviews.results_count > 0;

  /** For experiment-sake we will find the size tables from the product tabs
   *  TODO: When the experiment is over refactor this to for example another Contentful Content Type
   */
  const sizeTable = getSizeTableTab(productTabs);
  const intl = useIntl();
  const loadingMessage = intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' });
  const parsedQueryParams = new URLSearchParams(location.search);
  const previewImage = parsedQueryParams.get('productPreview');
  const {
    consentData: consentForSimilarProducts,
    data: similarProductsData,
    config: configSimilarProducts,
    isPlacementLoaded: placementLoadedSimilarProducts,
    anonymousCOAMToken: authTokenSimilarProducts,
  } = useCTRecommendations(
    'PLC-10001650',
    'Product Detail Page',
    pageContent.contentful_id,
    12000,
    null,
  );

  return (
    <>
      <div className="pd-h-5" />
      <div className={clsx('easy-pdp-main pd-relative pd-z-[1] pd-mb-0 pd-grid pd-gap-y-6 md:pd-mb-8 lg:pd-gap-6 lg:pd-bg-transparent', 'after:pd-bg-white')}>
        <Breadcrumb className="easy-pdp-breadcrumbs pd-text-black">
          <Breadcrumb.Item to={productCategory.path}>
            {productCategory.categoryName}
          </Breadcrumb.Item>
          <Breadcrumb.Item isCurrentPage>{productName || pageTitle}</Breadcrumb.Item>
        </Breadcrumb>

        <header className="easy-pdp-title">
          <Heading className="pd-mb-1 pd-text-dim-800">{pageTitle}</Heading>
          <Heading variant="display" as="h3" className="pd-text-dim-800">
            {subtitle}
          </Heading>
          <div className="pd-mt-2 pd-flex pd-gap-2">
            {labels?.map((label: Label) => (
              <Badge
                key={label.type}
                variant={getBadgeKind(label.type)}
              >
                {label.displayName}
              </Badge>
            ))}
          </div>
        </header>

        <div className="easy-pdp-gallery">
          <div className="pd-relative">
            <ProductGalleryBuilder
              gallery={gallery}
              title={title}
              productKey={selector?.product.key}
              scenes={scenes}
              previewImage={previewImage}
            />
          </div>
        </div>

        {gallery?.experimentId && (
        <div className="easy-pdp-gallery">
          <div className="pd-relative">
            <Experiment
              id={gallery.experimentId}
              fallback={<FallbackSpinnerComponent loadingMessage={loadingMessage} />}
            >
              <Experiment.Control>
                <ProductGalleryBuilder
                  gallery={gallery.control}
                  title={title}
                  productKey={selector?.product.key}
                  scenes={scenes}
                  previewImage={previewImage}
                />
              </Experiment.Control>
              <Experiment.Variant>
                <ProductGalleryBuilder
                  gallery={gallery.variant}
                  title={title}
                  productKey={selector?.product.key}
                  scenes={scenes}
                  previewImage={previewImage}
                />
              </Experiment.Variant>
            </Experiment>
          </div>
        </div>
        )}
        <div className="easy-pdp-selector pd-flex pd-flex-col pd-items-end pd-gap-5">
          <Experiment id="ninjas-exp1057_show_order_steps_on_pdp">
            <Experiment.Variant>
              <OrderProgress pageLayout={data.pageContent.pageLayout} />
            </Experiment.Variant>
          </Experiment>
          <ResetSelectorButton contentfulId={contentfulId} />
          <div
            className="pd-flex pd-min-h-screen pd-w-full pd-flex-col pd-border-t-[3px] pd-border-dim-200 pd-bg-white pd-px-4 pd-pb-6 md:pd-pt-2 lg:pd-rounded-[8px] lg:pd-border"
          >
            {isSelectorInitialized ? (
              <>
                {selector ? (
                  <DefaultValuesSelectorSection data-cy="easy-pdp-product-page" selector={selector} data={data} />
                ) : (
                  <div className="pd-mt-8">
                    <Empty
                      icon="exclamation"
                      title={intl.formatMessage({
                        id: 'product.configurator.outOfStock',
                        defaultMessage: 'This product is currently unavailable/out of stock',
                      })}
                      description={(
                        <FormattedMessage
                          id="product.configurator.outOfStockDescription"
                          defaultMessage="Contact our customer service or request a custom quote for an alternative"
                        />
                      )}
                    />
                  </div>
                )}
              </>
            ) : <FallbackSpinnerComponent loadingMessage={loadingMessage} />}
          </div>
        </div>
        <section className="easy-pdp-seo pd-px-6 md:pd-px-0">
          <Section>
            <Container>
              <ProductTabs productPage={pageContent} product={selector?.product} />
            </Container>
          </Section>
          {pageContent.productQuote ? (
            <PPAGBanner productQuote={pageContent.productQuote} />)
            : (
              <PersonalAssistance
                source="PDP-easy"
                text={(
                  <FormattedMessage
                    id="general.personal.advice.easy.text"
                    defaultMessage="personal assistance needed?"
                  />
            )}
              />
            )}
          <ExploreDesignTemplateButton label="pdp_button" isButton className="pd-my-4 pd-w-full" withLine={false} />
          {hasProductReviews && (
          <div className="product-page-reviews">
            <Container className="pd-pt-10">
              <ClientOnly>
                <Suspense>
                  {reviewSource === 'reviews_io' && (
                  <ReviewsIO
                    category={productCategory.categoryName}
                    contentfulId={contentfulId}
                    productName={productName}
                    productReviews={reviews}
                  />
                  )}
                </Suspense>
              </ClientOnly>
            </Container>
          </div>
          )}
        </section>
      </div>
      <Section variant="dim-branded" className="pd-bg-dim-100 pd-px-4 md:pd-px-0">
        <Container>
          <Grid className="pd-grid-cols-1 pd-gap-4 md:pd-grid-cols-2">
            <Grid.Item>
              {pageContent.description && (
              <RichText>
                <Markdown anchorTarget="_blank">{pageContent.description.description}</Markdown>
              </RichText>
              )}
              <ClientOnly>
                <Suspense>
                  {sizeTable && selector && (
                  <SizeTable
                    className="pd-mt-6"
                    sizeTable={sizeTable}
                    productName={productName}
                    contentfulId={contentfulId}
                    variantSku={selector.configuration.variantSku}
                  />
                  )}
                </Suspense>
              </ClientOnly>
              <ClientOnly>
                <Suspense>
                  {selector && (
                  <Experiment id="auto_generated_technical_drawnings">
                    <Experiment.Variant>
                      <TechnicalDrawing
                        productKey={selector.product.key}
                        productVersion={selector.product.productVersion}
                      />
                    </Experiment.Variant>
                    <Experiment.Control>
                      <TechnicalDrawingModal
                        productPage={pageContent}
                        variant={selector.configuration.variantSku}
                        pageLayout={pageContent.pageLayout}
                      />
                    </Experiment.Control>
                  </Experiment>
                  )}
                </Suspense>
              </ClientOnly>
            </Grid.Item>
            <Grid.Item>
              {pageContent.linkedFaqs && (
              <LinkedFaqs
                linkedFaqs={pageContent.linkedFaqs}
                productName={productName}
                contentful_id={contentfulId}
                variantSku={selector?.configuration.variantSku}
                pageName="product_detail_page"
              />
              )}
            </Grid.Item>
          </Grid>
        </Container>
      </Section>
      <StoreToggle id="drukwerkdeal-nl_printdeal-be">
        <StoreToggle.Enabled>
          <Recommendations
            config={configSimilarProducts}
            itemsPerRow={4}
            data={similarProductsData}
            consentData={consentForSimilarProducts}
            onView={(products: IProductPage[], analyticsId: string) =>
              handleRecommendationProductImpressions(
                'product_page_PPAG_similar_products',
                products,
                analyticsId,
                similarProductsData?.placements[0].placementId,
                similarProductsData?.placements[0].title,
              )}
            data-testid="similar-products-panel"
            isPlacementLoaded={placementLoadedSimilarProducts}
            anonymousCOAMToken={authTokenSimilarProducts}
            componentName="Product Page - Similar Products"
            overRideCookieConsentForTesting
            showHeader
            reviews={reviews}
          />
        </StoreToggle.Enabled>
      </StoreToggle>
    </>
  );
};

export default ProductDetailPPAG;
